import { getSandboxApiBaseUrl } from "@/lib/services/sandbox"
import { createApi, type Api } from "@/platform/api/api"
import { browserSessionOrigin } from "@/platform/api/browser-session-origin"
import { API_BASE_URL } from "@/platform/api/constants"
import { resolveIdentityClient } from "@/platform/api/identity.client"
import { requestHeaders } from "@/platform/api/request-headers"
import { currentViewer } from "@/platform/session/current-viewer"
import { suspendSession } from "@/platform/session/publisher"
import { handleUnauthorized } from "@/platform/session/sync"
import { holdViewerBoundary } from "@/platform/session/viewer-boundary"

import type { QueryClient } from "@tanstack/react-query"

interface Entry {
  api: Api
  held: () => boolean
}

const apis = new WeakMap<QueryClient, Map<string, Entry>>()

const baseUrl = (): string => (typeof window === "undefined" ? API_BASE_URL : (browserSessionOrigin(window.location.origin) ?? getSandboxApiBaseUrl() ?? API_BASE_URL))

export const browserApi = (queryClient: QueryClient, viewer: string): Api => {
  let byViewer = apis.get(queryClient)
  if (!byViewer) {
    byViewer = new Map()
    apis.set(queryClient, byViewer)
  }
  const existing = byViewer.get(viewer)
  if (existing?.held()) return existing.api
  const held = holdViewerBoundary()
  const fromCurrentViewer = (requester: string) => held() && currentViewer(queryClient) === requester
  const api = createApi({
    baseUrl: baseUrl(),
    credentials: "cookie-session",
    viewer,
    resolveHeaders: requestHeaders(resolveIdentityClient),
    onSessionIdentityChanged: (requester) => {
      if (fromCurrentViewer(requester)) suspendSession(queryClient)
    },
    onUnauthorized: (requester) => {
      if (fromCurrentViewer(requester)) handleUnauthorized(queryClient, requester)
    },
  })
  byViewer.set(viewer, { api, held })
  return api
}
